import React, { useState, useEffect, useContext, useRef } from 'react';
import SocketContext from '../context/SocketContext';
import { useUserContext } from '../context/UserContext';
import formatDate from '../components/formatDate';
import notify from '../context/Notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments, faPaperclip, faPaperPlane } from '@fortawesome/free-solid-svg-icons';

const Flud = () => {
  const { users, id } = useUserContext();
  const { socket } = useContext(SocketContext);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const chatContainer = useRef(null);

  const sendMessage = async (event) => {
    event.preventDefault();
    if (!newMessage.trim() && !selectedFile) return;

    let payload = {};

    if (newMessage.trim()) {
      payload.message = newMessage;
    }

    if (selectedFile) {
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onloadend = () => {
        payload.imageBase64 = reader.result.split(',')[1];
        emitMessage(payload);
      };
    } else {
      emitMessage(payload);
    }

    setNewMessage('');
    setSelectedFile(null);
    setPreviewImageUrl(null);
  };

  const emitMessage = (payload) => {
    const messageToSend = {
      user: id,
      message: payload.message || '',
      imageBase64: payload.imageBase64 || null,
      timestamp: new Date() * 1,
    };
    socket.emit('chat_message', messageToSend);
  };

  const getUser = (user) => {
    const u = users.findIndex((x) => x._id === user);
    if (u !== -1) {
      return users[u].name;
    }
    return '—';
  };

  const getLastMessages = async (offset) => {
    try {
      const hostname = "" //process.env.REACT_APP_API_URL;
      const response = await fetch(hostname + '/fluds/getMessages/' + offset, {
        method: 'get',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': JSON.parse(localStorage.getItem('authToken')).token
        }
      });
      const data = await response.json();
      for (let i = 0; i < data.data.length; i++) {
        setMessages((prevMsgs) => [...prevMsgs, data.data[i]]);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getLastMessages(0);
    if (!socket) alert("Не подключен к чату");
    // eslint-disable-next-line react-hooks/exhaustive-deps
    socket?.on('message', (msg) => {
      notify("Флудилка: " + getUser(msg.user) + ": " + msg.message);
      setMessages((prevMsgs) => [...prevMsgs, msg]);
    });
  }, []);

  useEffect(() => {
    if (chatContainer.current) {
      chatContainer.current.scrollTo({
        top: chatContainer.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  const handleFileSelect = (e) => {
    if (e.target.files.length === 0) return;
    const file = e.target.files[0];
    setSelectedFile(file);
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => {
      setPreviewImageUrl(reader.result);
    };
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(e);
    }
  };

  const getDayKey = (timestamp) => {
    const date = new Date(timestamp);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  const getDayLabel = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - target) / 86400000);

    if (diffDays === 0) return 'Сегодня';
    if (diffDays === 1) return 'Вчера';
    return date.toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: today.getFullYear() === target.getFullYear() ? undefined : 'numeric'
    });
  };

  const isSameGroup = (left, right) => {
    if (!left || !right) return false;
    return left.user === right.user && getDayKey(left.timestamp) === getDayKey(right.timestamp);
  };

  return (
    <div className="crm-page" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <div className="crm-breadcrumb">
        <span className="crm-breadcrumb__item">Рабочий стол</span>
        <span className="crm-breadcrumb__sep">›</span>
        <span className="crm-breadcrumb__item crm-breadcrumb__item--active">Флудилка</span>
      </div>

      <div className="crm-page-header">
        <h1 className="crm-page-title">
          <FontAwesomeIcon icon={faComments} style={{ marginRight: 10 }} />
          Флудилка
        </h1>
      </div>

      <div className="flud-wrapper">
        <div className="flud-messages" ref={chatContainer}>
          {messages.map((msg, index) => {
            const prevMsg = messages[index - 1];
            const nextMsg = messages[index + 1];
            const isFirstInDay = index === 0 || getDayKey(prevMsg.timestamp) !== getDayKey(msg.timestamp);
            const isGroupedWithPrev = isSameGroup(prevMsg, msg);
            const isGroupedWithNext = isSameGroup(msg, nextMsg);

            return (
            <React.Fragment key={index}>
              {isFirstInDay && (
                <div className="flud-day-separator">
                  <span className="flud-day-separator__label">{getDayLabel(msg.timestamp)}</span>
                </div>
              )}

              <div className={`flud-message ${msg.user === id ? 'flud-message--own' : 'flud-message--other'} ${isGroupedWithPrev ? 'flud-message--grouped-prev' : ''} ${isGroupedWithNext ? 'flud-message--grouped-next' : ''}`}>
                {!isGroupedWithPrev && msg.user !== id && (
                  <div className="flud-message__header">
                    <span className="flud-message__user">{getUser(msg.user)}</span>
                  </div>
                )}
                {msg.message && <p className="flud-message__text">{msg.message}</p>}
                {msg.imageBase64 && (
                  <img
                    className="flud-message__image"
                    alt="Загруженное изображение"
                    src={`data:image/jpeg;base64,${msg.imageBase64}`}
                    onClick={() => setViewingImage(`data:image/jpeg;base64,${msg.imageBase64}`)}
                    style={{ cursor: 'pointer' }}
                  />
                )}
                {!isGroupedWithNext && (
                  <div className="flud-message__footer">
                    <span className="flud-message__time">{formatDate(msg.timestamp)}</span>
                  </div>
                )}
              </div>
            </React.Fragment>
            );
          })}
        </div>

        <form className="flud-form" onSubmit={sendMessage}>
          <div className="flud-form__container">
            <textarea
              className="flud-form__textarea"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Введите сообщение..."
              rows={3}
            />

            <div className="flud-form__controls">
              {previewImageUrl && (
                <img className="flud-form__preview" src={previewImageUrl} alt="Предпросмотр" />
              )}

              <label className="flud-form__attach-btn" htmlFor="file-input">
                <FontAwesomeIcon icon={faPaperclip} />
              </label>
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                id="file-input"
                onChange={handleFileSelect}
              />

              <button type="submit" className="flud-form__send-btn">
                <FontAwesomeIcon icon={faPaperPlane} />
                <span>Отправить</span>
              </button>
            </div>
          </div>
        </form>
      </div>

      {viewingImage && (
        <div className="image-viewer-overlay" onClick={() => setViewingImage(null)}>
          <div className="image-viewer-container">
            <button
              className="image-viewer-close"
              onClick={() => setViewingImage(null)}
              aria-label="Закрыть просмотр"
            >
              ×
            </button>
            <img
              className="image-viewer-image"
              src={viewingImage}
              alt="Просмотр изображения"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Flud;